import { useEffect, useState } from 'react'
import DiscordIcon from './DiscordIcon'
import { useLanguage } from '@/contexts/LanguageContext'

/**
 * Live Discord server card for the landing page.
 *
 * Counts come from our own backend (/api/home/guild → Discord's guild lookup,
 * cached there), not from an embedded third-party widget: the page ships no
 * foreign script, and the numbers are the real ones rather than a static claim
 * that quietly goes stale.
 *
 * Renders nothing until it has real data. A card announcing "0 online" because
 * a fetch failed reads worse than no card at all — and the invite button is the
 * whole point, so there is nothing to show without it.
 *
 * The card is a Discord object, so it wears Discord's identity rather than the
 * page's: blurple accent, squircle server icon with a presence badge, and the
 * logo itself as the only decoration — drawn, not photographed. That also keeps
 * the site's crimson rationed to primary actions, and makes the invite the one
 * blurple thing on the page, which is exactly where the click should go.
 */

interface Guild {
  name: string
  icon_url: string
  online: number
  members: number
  invite_url: string
}

/** Discord blurple. Hard-coded on purpose — it is a brand colour, not a theme
 *  token, and it must not drift when the site palette changes. */
const BLURPLE = '#5865F2'

export default function DiscordServerCard() {
  const { T, lang } = useLanguage()
  const [guild, setGuild] = useState<Guild | null>(null)

  useEffect(() => {
    let alive = true
    fetch('/api/home/guild')
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (alive && d?.invite_url) setGuild(d) })
      .catch(() => { /* leave the card out */ })
    return () => { alive = false }
  }, [])

  if (!guild) return null

  const n = (v: number) => v.toLocaleString(lang === 'tr' ? 'tr-TR' : 'en-GB')

  return (
    <div className="relative w-full overflow-hidden rounded-md border border-white/12 bg-[#101015]">
      {/* Blurple light thrown from behind the icon — CSS, so it costs nothing
          and scales with the card instead of pixelating. */}
      <div
        aria-hidden
        className="pointer-events-none absolute -left-16 -top-24 h-56 w-56 rounded-full opacity-45 blur-3xl"
        style={{ background: `radial-gradient(circle, ${BLURPLE}, transparent 70%)` }}
      />
      {/* The logo, bleeding off the top-right corner as a watermark. */}
      <span
        aria-hidden
        className="pointer-events-none absolute -right-8 -top-10 opacity-[0.07]"
        style={{ color: BLURPLE }}
      >
        <DiscordIcon size={190} />
      </span>

      <div className="relative p-6">
        <div className="flex items-center gap-4">
          {guild.icon_url && (
            <div className="relative shrink-0">
              <img
                src={guild.icon_url}
                alt=""
                className="h-16 w-16 rounded-2xl object-cover"
              />
              {/* Presence badge, the way Discord marks a live avatar: a green
                  dot punched out of the card ground. */}
              <span
                aria-hidden
                className="absolute -bottom-0.5 -right-0.5 h-5 w-5 rounded-full border-4 border-[#101015] bg-green-500"
              />
            </div>
          )}

          <div className="min-w-0 flex-1">
            <p
              className="font-display text-[11px] font-bold uppercase tracking-[0.24em]"
              style={{ color: BLURPLE }}
            >
              {T.home.serverCardKicker}
            </p>
            <p className="mt-1.5 truncate font-display text-xl font-extrabold leading-tight tracking-tight">
              {guild.name}
            </p>
          </div>
        </div>

        {/* Counts on one line, Discord-widget style: the number carries the
            weight, the word after it only says what was counted. */}
        <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 rounded-md bg-white/[0.04] px-4 py-3.5">
          <span className="flex items-center gap-2">
            {/* Ping ring behind a solid dot — the standard "this number is
                live" tell. aria-hidden: it says nothing a reader needs. */}
            <span className="relative flex h-2.5 w-2.5 shrink-0" aria-hidden>
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-500 opacity-75" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-green-500" />
            </span>
            <span className="font-display text-lg font-extrabold leading-none tracking-tight">
              {n(guild.online)}
            </span>
            <span className="text-[13px] text-muted-foreground">{T.home.serverCardOnlineLabel}</span>
          </span>

          <span aria-hidden className="h-4 w-px bg-white/12" />

          <span className="flex items-center gap-2">
            <span className="font-display text-lg font-extrabold leading-none tracking-tight text-foreground/75">
              {n(guild.members)}
            </span>
            <span className="text-[13px] text-muted-foreground">{T.home.serverCardMembersLabel}</span>
          </span>
        </div>

        {/* Blurple button rather than the site's crimson: this leaves the page
            for Discord, and it should look like it does. Not <Button>, whose
            variants are all theme-coloured. */}
        <a
          href={guild.invite_url}
          target="_blank"
          rel="noreferrer"
          className="mt-5 flex h-12 w-full items-center justify-center gap-2.5 rounded-md font-display text-[13px] font-bold uppercase tracking-[0.12em] text-white transition-colors hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
          style={{ backgroundColor: BLURPLE }}
        >
          <DiscordIcon size={18} />
          {T.home.serverCardCta}
        </a>
      </div>
    </div>
  )
}
